/**
 * <QuickReplies> — les suggestions rapides d'une conversation (molecule).
 *
 * Une rangée de gestes tout prêts au-dessus du composeur : « Où j'en suis », « Indice »,
 * « Rappel de cours » avec Sam. Ce sont des ACTIONS, pas une sélection : chacune est un
 * `<Button size="sm">`, jamais une `<ChoiceChip>` (qui se coche). Au plus une est mise en avant
 * (`emphasis`, le bouton plein) ; les autres sont des secondaires.
 *
 * Une suggestion n'est jamais retirée pendant que l'autre répond : `disabled` l'estompe et elle
 * garde sa place, sinon le bas de l'écran se contracte puis se rouvre à chaque tour.
 *
 * Le poussoir `sm` fait 32 px : chaque suggestion est posée dans une cible de 44 px, qui presse
 * elle aussi. La rangée défile quand elle déborde.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { HorizontalList } from "../../atoms/HorizontalList"
import { type IconName } from "../../atoms/icons/Icon"

/** Une suggestion. À remonter dans `ports/primary/ui/types` au câblage. */
export type UIQuickReply = {
    key: string
    label: string
    iconName?: IconName
    /** Le geste mis en avant de la rangée : le bouton plein. Un seul. */
    emphasis?: boolean
    /** Son action est en cours (la photo part) : la roue prend la place du libellé. */
    loading?: boolean
    disabled?: boolean
}

export type QuickRepliesProps = {
    replies: UIQuickReply[]
    onSelect: (key: string) => void
    /** Toute la rangée est estompée et ne répond plus — l'autre répond, ou le plafond est atteint. */
    disabled?: boolean
    testID?: string
}

/** La cible d'un doigt, autour d'un poussoir de 32 px. */
const TOUCH_TARGET = 44

export function QuickReplies(props: QuickRepliesProps) {
    const { replies, onSelect, disabled = false, testID } = props

    if (replies.length === 0) return null

    return (
        <HorizontalList gap="2" testID={testID}>
            {replies.map((reply) => {
                const blocked = disabled || reply.disabled === true || reply.loading === true
                const press = () => onSelect(reply.key)
                return (
                    // La cible de 44 px : elle presse comme le bouton qu'elle entoure, sans être un second
                    // bouton pour un lecteur d'écran ni un second arrêt au clavier.
                    <Pressable
                        key={reply.key}
                        onPress={press}
                        disabled={blocked}
                        accessible={false}
                        focusable={false}
                        style={styles.target}
                    >
                        <Button
                            label={reply.label}
                            iconName={reply.iconName}
                            size="sm"
                            variant={reply.emphasis === true ? "solid" : "outline"}
                            onPress={press}
                            disabled={disabled || reply.disabled === true}
                            loading={reply.loading === true}
                        />
                    </Pressable>
                )
            })}
        </HorizontalList>
    )
}

const styles = StyleSheet.create(() => ({
    target: {
        minHeight: TOUCH_TARGET,
        justifyContent: "center",
    },
}))
